/**
 * Mosquito Module Types
 *
 * UI Naming Convention:
 * - Site fields: site* prefix (siteName, siteCity, siteActive, etc.)
 * - Pool fields: pool* prefix (poolEpiStatus, poolDateCollected, etc.)
 *
 * Single conversion point: UI → API mapping happens in store CRUD methods.
 */

import { z } from "zod";

// =============================================================================
// ZOD SCHEMAS
// =============================================================================

/**
 * Pool schema - validates pool form data
 */
export const poolSchema = z.object({
  poolId: z.number().optional(),
  poolEpiStatus: z.string().min(1, "Epi status is required"),
  poolDateCollected: z.string().min(1, "Date collected is required"),
  poolDateReported: z.string().min(1, "Date reported is required"),
  poolNumberInPool: z.number().optional(),
  poolIsNotified: z.boolean().optional(),
  poolActive: z.boolean().default(true),
  poolSpecies: z.string().min(1, "Species is required"),
  poolCulture: z.string().optional(),
  poolTrapType: z.string().optional(),
  poolTestingMethod: z.string().min(1, "Testing method is required"),
  poolLab: z.string().optional(),
  poolLabSpecimenId: z.string().optional(),
  poolNote: z.string().optional(),
});

/**
 * Site schema - validates site form data
 */
export const siteSchema = z.object({
  siteId: z.number().optional(),
  siteName: z.string().min(2, "Site name is required"),
  siteOrganization: z.string().min(2, "Organization is required"),
  siteAddress: z.string().optional(),
  siteCity: z.string().min(2, "City is required"),
  siteState: z.string().optional(),
  siteZip: z.string().min(3, "ZIP code is required"),
  siteCounty: z.string().min(2, "County is required"),
  siteIsGeolocated: z.boolean().default(false),
  siteActive: z.boolean().default(true),
  siteLatitude: z.string().optional().nullable(),
  siteLongitude: z.string().optional().nullable(),
  siteGpsNote: z.string().optional(),
  sitePrimaryContactFirstName: z.string().optional(),
  sitePrimaryContactLastName: z.string().optional(),
  sitePrimaryContactPhone: z.string().optional(),
  sitePrimaryContactEmail: z.string().optional(),
  siteSecondaryContactFirstName: z.string().optional(),
  siteSecondaryContactLastName: z.string().optional(),
  siteSecondaryContactPhone: z.string().optional(),
  siteSecondaryContactEmail: z.string().optional(),
  siteComment: z.string().optional(),
  pools: z.array(poolSchema).optional(),
});

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type PoolFormInput = z.infer<typeof poolSchema>;
export type SiteFormInput = z.infer<typeof siteSchema>;

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

/**
 * API response type for pool summary data (from backend)
 */
export interface PoolSummaryApiResponse {
  id: number;
  county?: string | null;
  city?: string | null;
  siteName?: string | null;
  siteId: number;
  epiStatus?: string | null;
  species?: string | null;
  dateCollected: string;
  dateReported: string;
}

/**
 * UI representation of pool summary (for IndexPage table)
 */
export interface PoolSummary {
  poolId: number;
  siteId: number;
  siteCounty: string;
  siteCity: string;
  siteName: string;
  poolEpiStatus: string;
  poolSpecies: string;
  poolDateCollected: string;
  poolDateReported: string;
}

// =============================================================================
// EMPTY FORM DEFAULTS
// =============================================================================

export const emptyPoolForm: PoolFormInput = {
  poolId: undefined,
  poolEpiStatus: "",
  poolDateCollected: "",
  poolDateReported: "",
  poolNumberInPool: undefined,
  poolIsNotified: false,
  poolActive: true,
  poolSpecies: "",
  poolCulture: "",
  poolTrapType: "",
  poolTestingMethod: "",
  poolLab: "",
  poolLabSpecimenId: "",
  poolNote: "",
};

export const emptySiteForm: SiteFormInput = {
  siteId: undefined,
  siteName: "",
  siteOrganization: "",
  siteAddress: "",
  siteCity: "",
  siteState: "",
  siteZip: "",
  siteCounty: "",
  siteIsGeolocated: false,
  siteActive: true,
  siteLatitude: null,
  siteLongitude: null,
  siteGpsNote: "",
  sitePrimaryContactFirstName: "",
  sitePrimaryContactLastName: "",
  sitePrimaryContactPhone: "",
  sitePrimaryContactEmail: "",
  siteSecondaryContactFirstName: "",
  siteSecondaryContactLastName: "",
  siteSecondaryContactPhone: "",
  siteSecondaryContactEmail: "",
  siteComment: "",
  pools: [{ ...emptyPoolForm }],
};
